import {Identifier, makeNumericId} from "../common/Identifier";
import {TimesheetTask} from "../timesheet/TimesheetTask";
import {ProjectBillType, ProjectNoneInvoiceItem} from "./ProjectBillType";
import {makeSensitive, Sensitive} from "../common/Sensitive";
import {WorkingHours} from "../timesheet/WorkingHours";
import {Money, Percent} from "../accounting/Finance";
import {ProjectStagePhase} from "./ProjectStagePhase";
import {
    ProjectBillablePerformanceResource,
    ProjectResourceable,
    ProjectResourceContainer,
    ProjectTimeableResource
} from "./ProjectResourceable";
import {ProjectStagePhaseIdPath} from "./Project";
import {Nameable} from "../common/Nameable";
import {Performanceable} from "../accounting/Performanceable";
import {Nullable} from "../common/Nullable";
import {ProjectStage} from "./ProjectStage";
import {ProjectStatusProp, ProjectStatusByName} from "./ProjectStatusProp";
import {SoftDeletable} from "../common/SoftDeletable";
import {TimeRetailPricePerHour, TimeRetailPricePerHourType} from "./TimeRetailPricePerHourType";

/**
 * Represents the module used to manage and identify stages, phases, and tasks within a project lifecycle.
 *
 * This variable serves as a unique identifier to standardize references across systems or components
 * that deal with various stages of project development. It ensures consistency and helps in tracking
 * workflows effectively from start to finish.
 *
 * @constant
 */
export const ProjectStagePhaseTaskModule = {
    identifier: "project_stage_phase_task"
};

/**
 * Represents a unique identifier for a specific task within a project stage and phase.
 *
 * This interface models the connection between a project stage, its phase, and the individual task.
 * It ensures each task is uniquely identifiable within the given hierarchy.
 *
 * @property projectStagePhaseTaskId - A strongly-typed, number-based identifier encapsulated
 * within an `Identifier` type, used to uniquely distinguish a task tied to a project’s stage and phase.
 */
export interface ProjectStagePhaseTaskId {
    projectStagePhaseTaskId: Identifier<number>
}

/**
 * Represents a task within a specific phase of a project stage, including its budgeting,
 * billing, and performance attributes.
 *
 * This interface consolidates multiple task-related behaviors, such as time tracking,
 * budget planning, resource allocation, and performance monitoring, to provide a
 * comprehensive representation of a project phase task.
 */
export interface ProjectStagePhaseTask extends ProjectStagePhaseTaskId,
    ProjectResourceable, Nameable,
    ProjectResourceContainer,
    ProjectBillablePerformanceResource,
    ProjectTimeableResource,
    Performanceable,
    ProjectStatusProp,
    SoftDeletable
{
    phaseIdPath: ProjectStagePhaseIdPath
    task: TimesheetTask
    plannedBudget: ProjectStagePhaseTaskPlannedBudget
    additionalCosts: Percent
    projectBillType: ProjectBillType
}

/**
 * Represents the unique path to identify a specific task within a project, stage, and phase.
 *
 * This interface extends `ProjectStagePhaseIdPath`, adding the ability to uniquely pin down a task within a given phase.
 * It is used to ensure consistent task identification across different layers of the application.
 *
 * @extends ProjectStagePhaseIdPath
 *
 * @property taskId - A unique identifier for the task within the context of the project. This identifier must be a numeric value wrapped in an `Identifier` type.
 * This property enforces that each task is distinguishable from others, even within the same project phase.
 */
export interface ProjectStagePhaseTaskIdPath extends ProjectStagePhaseIdPath {
    taskId: Identifier<number>
}

/**
 * Determines whether the given object represents a ProjectStagePhaseTask.
 *
 * This utility function helps differentiate a specific type of resource,
 * `ProjectStagePhaseTask`, from other possible resource types. It checks the
 * presence of the `projectStagePhaseTaskId` property to make this determination.
 *
 * @param obj - The object to be evaluated, expected to adhere to the
 * `ProjectResourceable` interface.
 * @return True if the object contains the `projectStagePhaseTaskId` property,
 * indicating that it is a `ProjectStagePhaseTask`. False otherwise.
 * @throws This function does not throw any errors but expects that the input
 * object meets the basic requirements of the `ProjectResourceable` interface.
 */
export function isPhaseTask( obj: ProjectResourceable ): obj is ProjectStagePhaseTask {


    return obj?.hasOwnProperty("projectStagePhaseTaskId") ?? false;
}

/**
 * Represents the detailed budget planning for a specific task within a project's stage and phase.
 *
 * This interface provides a structured way to define key financial and effort-related metrics
 * used for project planning and decision-making. These metrics help teams estimate labor, costs,
 * and profit for specific tasks in the project lifecycle.
 */
export interface ProjectStagePhaseTaskPlannedBudget {
    manHours: WorkingHours
    profitMargin: Percent
    purchasePricePerHour: Sensitive<Money>
}

/**
 * Creates a default project stage phase task with predefined properties.
 *
 * This function ensures the creation of a minimal, valid `ProjectStagePhaseTask` to be used as a baseline
 * when no specific task details are provided. It supports applications where tasks must exist even if
 * only minimal information is available.
 *
 * The method prioritizes the provided `phase` and `stage` objects to inherit relevant task details.
 * When these are unavailable, default fallback values are used instead.
 *
 * @param phaseTaskId The unique numeric identifier for the phase task.
 * @param phase An optional reference to the current project phase. If provided, its details will be inherited.
 * @param stage An optional reference to the current project stage. Defaults will be applied if phase is unavailable.
 * @return A fully constructed `ProjectStagePhaseTask` object with default properties and derived attributes.
 * @throws Throws an error if critical dependent utilities like `makeSensitive` or `makeNumericId` fail to execute.
 */
export function defaultPhaseTask(
    phaseTaskId: number,
    phase: Nullable< ProjectStagePhase > = null,
    stage: Nullable< ProjectStage > = null
): ProjectStagePhaseTask {

    const sensitiveMoney = makeSensitive( 0.0 );
    const pTaskId = makeNumericId( phaseTaskId );

    return {
        task: {
            timesheetTaskId: makeNumericId( 0 ),
            name: "",
            isDeleted: false,
            taskFlags: {},
            lockStatus: 0,
            phaseTemplate: { id: 0, value: "-" }
        },
        status: phase?.status
            ?? stage?.status
            ?? ProjectStatusByName.INACTIVE,
        projectStagePhaseTaskId: pTaskId,
        phaseIdPath: {
            phaseId: phase?.phaseId ?? makeNumericId( 0 ),
            projectId: phase?.projectId ?? stage?.projectId ?? makeNumericId( 0 ),
            projectStageId: phase?.projectStageId ?? stage?.projectStageId ?? makeNumericId( 0 )
        },
        isContractedWork: false,
        internalOrderRetailPrice: sensitiveMoney,
        contractedOrderRetailPrice: sensitiveMoney,
        name: "",
        retailPriceUsed: sensitiveMoney,
        purchasePrice: sensitiveMoney,
        retailPrice: sensitiveMoney,
        projectBillType: ProjectNoneInvoiceItem,
        performance: 0.0,
        timePeriod:  {
            startAt: phase?.startAt ?? stage?.startAt ?? null,
            finishAt: phase?.finishAt ?? stage?.finishAt ?? null
        },
        additionalCosts: 0.0,
        plannedBudget: {
            manHours: 0.0,
            profitMargin: 10.0,
            purchasePricePerHour: makeSensitive( 60.0 )
        },
        resources: [],
        resourceType: "ProjectStagePhaseTask",
        resourceTypeIdentifier: { rawValue: "ProjectStagePhaseTask" },
        isDeleted: false
    };

}